import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../components/Spin";
import { API, fileUpload, get } from "../../config";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import CustomLabelText from "../../components/CustomLabelText";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { AddTaskSchema } from "../../lib/Validation";
import { Loader2 } from "lucide-react";
import JobSelect from "../../components/JobSelect";

const AddEditTask = ({
  id,
  toggleShowAddEditTask,
  edit,
  fetchTaskData,
  trustId,
}) => {
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [jobLoading, setJobLoading] = useState(false);
  const [jobListing, setJobListing] = useState([]);
  const [jobList, setJobList] = useState([]);

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_TASKS}/` + id);

      setTaskData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setTaskData(null);
    }
  }
  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value, { setErrors, resetForm }) => {
    const formData = new FormData();

    formData.append("trust_id", trustId);
    formData.append("ts_job_id", value?.ts_job_id ? value?.ts_job_id : "");
    formData.append("title", value?.title ? value?.title : "");
    formData.append("code", value?.code ? value?.code : "");
    formData.append(
      "description",
      value?.description ? value?.description : ""
    );

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.TASK_ADD}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditTask();
        fetchTaskData && fetchTaskData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobList) {
      let jobTempListing = jobList?.map((item) => ({
        ...item,
        value: item.id,
        label: `${item?.code ? item?.code : ""}`,
      }));
      setJobListing(jobTempListing || []);
    } else {
      setJobListing([]);
    }
  }, [jobList]);

  return (
    <Formik
      initialValues={{
        ts_job_id: taskData?.ts_job_id,
        title: taskData?.title,
        code: taskData?.code,
        description: taskData?.description,
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddTaskSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} fullscreen={true}/>}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid gap-2 col-span-2">
                  <div className="min-w-0">
                    <Label htmlFor="ts_job_id">
                      <CustomLabelText
                        labelText="Job/Matter"
                        errorField={errors?.ts_job_id}
                      />
                    </Label>
                    <div className="relative mt-2">
                      <JobSelect
                        trustId={trustId}
                        frameworkData={jobListing} // List of vendors
                        optionType="Job/Matter"
                        handleSelect={(e) => {
                          setFieldValue(`ts_job_id`, e);
                        }}
                        name="ts_job_id"
                        disabled={false}
                        initialValue={
                          values?.ts_job_id ? values?.ts_job_id : ""
                        }
                        setJobList={setJobList} // Update vendor list as new data loads
                        setJobLoading={setJobLoading} // Control loading state
                        onClear={() => {
                          setFieldValue(`ts_job_id`, "");
                        }}
                        className={
                          errors?.job_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        loading={jobLoading}
                      />
                      {jobLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid gap-2 col-span-2">
                  <Label htmlFor="title">
                    <CustomLabelText
                      required={true}
                      labelText="Title"
                      errorField={errors?.title}
                    />
                  </Label>
                  <Input
                    id="title"
                    placeholder="Enter Name"
                    required
                    name="title"
                    onChange={(e) => {
                      setFieldValue("title", e?.target?.value);
                    }}
                    onBlur={handleBlur}
                    value={values?.title}
                    className={
                      errors?.title &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                  />
                </div>
                <div className="grid gap-2 col-span-2">
                  <Label htmlFor="code">
                    <CustomLabelText
                      required={true}
                      labelText="Code"
                      errorField={errors?.code}
                    />
                  </Label>
                  <Input
                    id="code"
                    placeholder="Enter Name"
                    required
                    name="code"
                    onChange={(e) => {
                      setFieldValue("code", e?.target?.value);
                    }}
                    onBlur={handleBlur}
                    value={values?.code}
                    className={
                      errors?.code &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                  />
                </div>
                <div className="grid gap-2 col-span-2">
                  <Label htmlFor="description">
                    <CustomLabelText
                      required={false}
                      labelText="Description"
                      errorField={errors?.description}
                    />
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={values?.description ? values?.description : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    onKeyPress={(e) => {
                      e.which === 13 && e.preventDefault();
                    }}
                    className={
                      errors?.description &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditTask();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditTask;
